import { forwardRef, memo, Ref, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { getUserEducation } from '~/actions';
import { getShouldShowFrontDeskSpecialty } from '~/growthbook';
import { useAppSelector } from '~/hooks';

import { filterObjectKeys } from '~/utils/Object';
import { Value } from './Field';
import { Radio } from './Radio';
import { IInputJobProps, TOptionValue } from './Types';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { EnumHelper } from '~/utils/EnumHelper';
import { SolidToothIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';

/**
 * Props for the Specialty component.
 * @interface ISpecialtyProps
 * @extends IInputJobProps<string>
 */
interface ISpecialtyProps extends IInputJobProps<string> {
  /** The professional type to filter specialties for */
  professional: string;
}

const Component = (
  {
    defaultValue,
    isDisabled = false,
    isOpen = false,
    professional,
    value: controlledValue,
    onChange,
    onToggle,
  }: ISpecialtyProps,
  ref: Ref<HTMLDivElement>,
) => {
  const dispatch = useDispatch();

  const educations = useAppSelector((state) => state.user.userEducation);

  useEffect(() => {
    if (!professional) return;

    const professionCode = UserProfession.getNormalizedCode(professional);
    dispatch(getUserEducation({ profession: professionCode }));
  }, [dispatch, professional]);

  const options: TOptionValue<string>[] = useMemo(() => {
    if (!professional) return [];

    const isSubProfession = EnumHelper.getObjectKeys(UserSubProfession).includes(professional);
    const professionCode = isSubProfession
      ? UserSubProfession.getProfession(professional)
      : professional;
    const normalizedProfessionCode = UserProfession.getNormalizedCode(professionCode || '');
    // @ts-expect-error - not typed
    const specialties = (educations as any)?.[normalizedProfessionCode]?.specialtiesAndProcedures;
    const keys = Object.keys(filterObjectKeys(specialties, 'specialty'));

    const filteredKeys = !getShouldShowFrontDeskSpecialty()
      ? keys.filter((it) => it !== 'specialty_Front Desk')
      : keys;

    return filteredKeys.map((key) => ({
      label: key.split('_')?.[1] || key,
      value: key,
    }));
  }, [professional, educations]);

  const CustomValueComponent = useCallback((currentValue: string | undefined) => {
    const displayValue = (currentValue as string)?.split('_')?.[1] || 'General';
    return <Value text={displayValue} />;
  }, []);

  return (
    <Radio
      IconSrc={<SolidToothIcon size={24} color={colors.secondary[500]} />}
      ValueComponent={CustomValueComponent}
      defaultValue={defaultValue}
      isDisabled={isDisabled}
      isOpen={isOpen}
      value={controlledValue}
      onChange={onChange}
      onToggle={onToggle}
      options={options}
      title="Specialty:"
      testName="input-job.specialty"
      ref={ref}
    />
  );
};

/**
 * A specialty selection dropdown component for dental professionals.
 *
 * This component provides a dropdown interface for selecting dental specialties
 * based on the professional type. It fetches available specialties from the user
 * education data and filters them based on feature flags and professional type.
 *
 * Features:
 * - Supports both controlled and uncontrolled modes
 * - Automatically fetches specialty data based on professional type
 * - Feature flag support for Front Desk specialty visibility
 * - Radio button selection interface
 * - Development mode warnings for control mode switching
 *
 * @example
 * ```tsx
 * // Controlled mode
 * <Specialty
 *   professional="dentist"
 *   value={selectedSpecialty}
 *   onChange={setSelectedSpecialty}
 *   isOpen={isDropdownOpen}
 *   onToggle={toggleDropdown}
 * />
 *
 * // Uncontrolled mode
 * <Specialty
 *   professional="hygienist"
 *   defaultValue="specialty_General"
 *   onChange={handleSpecialtyChange}
 * />
 * ```
 *
 * @component
 * @param props - Component props extending IInputJobProps
 * @param props.professional - The professional type to filter specialties for
 * @param props.defaultValue - Default selected specialty value (uncontrolled mode)
 * @param props.value - Current selected specialty value (controlled mode)
 * @param props.onChange - Callback fired when specialty selection changes
 * @param props.isDisabled - Whether the component is disabled
 * @param props.isOpen - Whether the dropdown is open
 * @param props.onToggle - Callback fired when dropdown is toggled
 * @param ref - React ref forwarded to the root element
 */
export const Specialty = memo(forwardRef(Component)) as <T = HTMLDivElement>(
  props: ISpecialtyProps & { ref?: Ref<T> },
) => ReturnType<typeof Component>;
